import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft } from 'lucide-react';
import { useSiteContent } from '../context/SiteContentContext';
import { usePageTitle } from '../hooks/usePageTitle';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LegalPage() {
  const { pageId } = useParams();
  const [page, setPage] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const { language } = useSiteContent();
  
  // Set page title based on legal page type
  const titleMap = {
    'privacy-policy': 'privacyPolicy',
    'terms-of-service': 'termsOfService',
    'cookie-policy': 'cookiePolicy'
  };
  usePageTitle(titleMap[pageId] || 'privacyPolicy');

  useEffect(() => {
    const fetchPage = async () => {
      try {
        const response = await axios.get(`${API}/legal/${pageId}`);
        setPage(response.data);
      } catch (error) {
        console.error('Error fetching legal page:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchPage();
  }, [pageId]);

  if (isLoading) {
    return (
      <div className="pt-20 min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-[#C41E3A] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!page) {
    return (
      <div className="pt-20 min-h-screen bg-slate-50 flex items-center justify-center">
        <p className="text-slate-500">Page not found</p>
      </div>
    );
  }

  const title = language === 'bg' && page.title_bg ? page.title_bg : page.title_en;
  const content = language === 'bg' && page.content_bg ? page.content_bg : page.content_en;

  return (
    <div className="pt-20 min-h-screen bg-slate-50">
      {/* Hero */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <Link to="/" className="inline-flex items-center text-slate-500 hover:text-[#C41E3A] mb-6 transition-colors">
            <ArrowLeft className="w-4 h-4 mr-2" />
            {language === 'bg' ? 'Обратно към начало' : 'Back to Home'}
          </Link>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-slate-900">
            {title}
          </h1>
          {page.last_updated && (
            <p className="text-slate-500 mt-4 text-sm">
              {language === 'bg' ? 'Последна актуализация: ' : 'Last updated: '}
              {new Date(page.last_updated).toLocaleDateString(language === 'bg' ? 'bg-BG' : 'en-GB', {
                day: 'numeric',
                month: 'long',
                year: 'numeric'
              })}
            </p>
          )}
        </div>
      </section>

      {/* Content */}
      <section className="py-12 md:py-16">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <div 
            className="prose prose-slate max-w-none prose-headings:text-slate-900 prose-p:text-slate-600 prose-li:text-slate-600 prose-a:text-[#C41E3A]"
            dangerouslySetInnerHTML={{ __html: content.replace(/\n/g, '<br />') }}
          />
        </div>
      </section>
    </div>
  );
}
